import React, {useCallback, useEffect, useState} from 'react';
import {useBlocker} from './usePrompt';
import {DiaPromptConfirmation} from './DiaConfimation';

type AllowNavigation = (next: {pathname: string}) => boolean;

type UnsavedChangesGuardArgs = {
    isDirty: boolean,
    save: () => void,
    allowNavigation?: AllowNavigation
};

type BlockerTransaction = {
    location: {pathname: string},
    retry: () => void
};

/**
 * Shared unsaved-changes guard: while the form is dirty, navigation is blocked
 * and DiaPromptConfirmation offers save / discard / return-to-edit. When the
 * form is clean the blocker is not registered, so navigation is free.
 *
 * `allowNavigation` lets an editor permit specific navigations without a prompt
 * (e.g. staying under the current entity's sub-pages).
 *
 * `allowNextNavigation` grants a one-shot allowance for a programmatic
 * navigation that follows a successful save (e.g. create-then-navigate); the
 * allowance is consumed by the next blocked transition.
 */
export function useUnsavedChangesGuard({isDirty, save, allowNavigation}: UnsavedChangesGuardArgs) {
    const [isRedirectAllowed, setIsRedirectAllowed] = useState(false);
    const [redirectCallback, setRedirectCallback] = useState<BlockerTransaction | null>(null);

    const blocker = (tx: BlockerTransaction) => {
        if (isRedirectAllowed) {
            // a one-shot allowance is pending (set right before a programmatic
            // navigate): let this transition through and consume the allowance
            setIsRedirectAllowed(false);
            tx.retry();
            return;
        }
        if (allowNavigation && allowNavigation(tx.location)) {
            setRedirectCallback(null);
            tx.retry();
        } else {
            setRedirectCallback(tx);
        }
    };

    // ignore the save state and change URL regardless
    const discardChanges = useCallback(() => {
        setIsRedirectAllowed(true);
        redirectCallback?.retry();
    }, [redirectCallback]);

    // go back to editing: clear the block and forget it existed
    const returnToEdit = useCallback(() => {
        setRedirectCallback(null);
    }, []);

    // do save, but do not redirect
    const doSave = useCallback(() => {
        save();
        setRedirectCallback(null);
    }, [save]);

    useEffect(() => {
        if (isRedirectAllowed && redirectCallback) {
            redirectCallback.retry();
            setRedirectCallback(null);
            setIsRedirectAllowed(false);
        }
    }, [isRedirectAllowed, redirectCallback]);

    useBlocker(blocker, isDirty);

    const confirmation = <DiaPromptConfirmation isActive={!!redirectCallback} discard={discardChanges} returnToEdit={returnToEdit} save={doSave} saveFailed={false}/>;

    const allowNextNavigation = useCallback(() => {
        setIsRedirectAllowed(true);
    }, []);

    return {confirmation, allowNextNavigation};
}
